import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Sparkles, Target, TrendingUp, Loader2, Copy, 
  ExternalLink, Share2, Link2, Zap, BarChart3, 
  Hash, Image as ImageIcon, Globe, Users, CheckCircle, Clock
} from "lucide-react";
import MainLayout from "../components/MainLayout";
import SEO, { pageSEO } from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromoteYourSitePage = () => {
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [businessDescription, setBusinessDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState("google");
  const [copiedItem, setCopiedItem] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!websiteUrl) return;
    
    setLoading(true);
    setResults(null);
    
    try {
      const res = await axios.post(`${API}/promote/analyze`, {
        website_url: websiteUrl,
        business_description: businessDescription
      });
      setResults(res.data);
      toast.success("Ad campaigns generated successfully!");
    } catch (err) {
      toast.error("Failed to analyze website. Please try again.");
    }
    setLoading(false);
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedItem(null), 2000);
  };

  const copyAllToClipboard = (items, label) => {
    const text = Array.isArray(items) ? items.join("\n") : items;
    navigator.clipboard.writeText(text);
    toast.success(`All ${label} copied!`);
  };

  const CopyButton = ({ text, id, small = false }) => (
    <button
      onClick={() => copyToClipboard(text, id)}
      className={`${small ? 'p-1' : 'p-2'} rounded-lg transition-all hover:scale-105 hover:bg-slate-700`}
    >
      {copiedItem === id ? (
        <CheckCircle className={`${small ? 'w-4 h-4' : 'w-5 h-5'} text-green-400`} />
      ) : (
        <Copy className={`${small ? 'w-4 h-4' : 'w-5 h-5'} text-slate-400 hover:text-white`} />
      )}
    </button>
  );

  const tabs = [
    { id: "google", label: "Google Ads", icon: Target, color: "amber" },
    { id: "facebook", label: "Facebook Ads", icon: Users, color: "blue" },
    { id: "social", label: "Social Media", icon: Share2, color: "pink" }
  ];

  const targetAudiences = [
    "IT service providers", "Local businesses", "Online shops", "Trades & home services",
    "Beauty & wellness", "Restaurants", "Handmade shops"
  ];

  return (
    <MainLayout>
      <SEO {...pageSEO.promoteYourSite} />
      
      {/* Hero Section */}
      <section className="pt-8 pb-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-transparent to-transparent" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute top-40 right-1/4 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl" />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              AI-Powered Advertising
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight">
              SmartIT <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Ad Generator</span>
            </h1>
            <p className="text-base md:text-lg text-slate-400 max-w-3xl mx-auto mb-2">
              Scan any website and instantly create complete Google Ads, Facebook Ads, and Social Media content.
            </p>
            <p className="text-sm text-slate-500 max-w-2xl mx-auto">
              Professional ad copy, social posts, hashtags, and strategies — generated in seconds.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Input Form */}
      <section className="py-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <form onSubmit={handleAnalyze} className="relative">
              <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 md:p-8 shadow-2xl">
                <div className="mb-5">
                  <label className="block text-sm font-medium text-slate-300 mb-2">Website URL</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Globe className="w-5 h-5 text-slate-500" />
                    </div>
                    <input
                      type="text"
                      required
                      className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-4 pl-12 pr-4 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                      placeholder="www.yourwebsite.com"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Additional Info <span className="text-slate-500">(optional)</span>
                  </label>
                  <textarea
                    className="w-full bg-slate-800/50 border border-slate-700 rounded-xl py-3 px-4 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all resize-none"
                    rows={2}
                    placeholder="Special offers, target audience, unique selling points..."
                    value={businessDescription}
                    onChange={(e) => setBusinessDescription(e.target.value)}
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading || !websiteUrl}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg py-4 px-6 rounded-xl transition-all flex items-center justify-center gap-3 shadow-lg shadow-blue-500/20 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin" />
                      <span>Generating Ad Campaigns...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-6 h-6" />
                      <span>Generate Ad Campaigns</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Results Section */}
      {results && (
        <section className="py-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              
              {/* Business Info Banner */}
              {results.business_info && (
                <div className="mb-6 bg-slate-900/60 border border-slate-800 rounded-xl p-4 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h3 className="text-white font-semibold">{results.business_info.name || "Your Business"}</h3>
                    <p className="text-slate-400 text-sm">{results.business_info.category} • {results.business_info.location}</p>
                  </div>
                  {results.business_info.is_it_services && (
                    <span className="px-3 py-1 bg-green-500/10 border border-green-500/20 text-green-400 rounded-full text-sm">
                      IT Services Optimized
                    </span>
                  )}
                </div>
              )}

              {/* Tabs */}
              <div className="flex gap-2 mb-6 p-1 bg-slate-900/60 border border-slate-800 rounded-xl w-fit">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                      activeTab === tab.id
                        ? "text-white shadow-md"
                        : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                    }`}
                    style={activeTab === tab.id ? {
                      backgroundColor: tab.color === 'blue' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                      color: tab.color === 'blue' ? '#60a5fa' : '#60a5fa'
                    } : {}}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Google Ads Tab */}
              {activeTab === "google" && results.google_ads && (
                <div className="space-y-6">
                  {/* Headlines */}
                  <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                    <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between bg-blue-500/5">
                      <h3 className="text-white font-semibold flex items-center gap-2">
                        <Target className="w-4 h-4 text-blue-400" />
                        Headlines ({results.google_ads.headlines?.length || 0})
                      </h3>
                      <button
                        onClick={() => copyAllToClipboard(results.google_ads.headlines, "headlines")}
                        className="text-blue-400 text-sm hover:text-blue-300 flex items-center gap-1"
                      >
                        <Copy className="w-4 h-4" /> Copy All
                      </button>
                    </div>
                    <div className="p-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {results.google_ads.headlines?.map((headline, idx) => (
                          <div key={`headline-${headline}-${idx}`} className="flex items-center justify-between bg-slate-800/50 rounded-lg px-3 py-2 group">
                            <span className="text-slate-200 text-sm">{headline}</span>
                            <CopyButton text={headline} id={`headline-${idx}`} small />
                          </div>
                        ))}
                      </div>
                      <p className="text-slate-500 text-xs mt-3">Max 30 characters each</p>
                    </div>
                  </div>

                  {/* Long Headlines */}
                  {results.google_ads.long_headlines?.length > 0 && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
                        <h3 className="text-white font-semibold">Long Headlines</h3>
                        <button
                          onClick={() => copyAllToClipboard(results.google_ads.long_headlines, "long headlines")}
                          className="text-blue-400 text-sm hover:text-blue-300 flex items-center gap-1"
                        >
                          <Copy className="w-4 h-4" /> Copy All
                        </button>
                      </div>
                      <div className="p-5 space-y-2">
                        {results.google_ads.long_headlines?.map((headline, idx) => (
                          <div key={`long-headline-${headline.substring(0,20)}-${idx}`} className="flex items-center justify-between bg-slate-800/50 rounded-lg px-4 py-3">
                            <span className="text-slate-200">{headline}</span>
                            <CopyButton text={headline} id={`long-headline-${idx}`} small />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Descriptions */}
                  <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                    <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
                      <h3 className="text-white font-semibold">Descriptions ({results.google_ads.descriptions?.length || 0})</h3>
                      <button
                        onClick={() => copyAllToClipboard(results.google_ads.descriptions, "descriptions")}
                        className="text-blue-400 text-sm hover:text-blue-300 flex items-center gap-1"
                      >
                        <Copy className="w-4 h-4" /> Copy All
                      </button>
                    </div>
                    <div className="p-5 space-y-3">
                      {results.google_ads.descriptions?.map((desc, idx) => (
                        <div key={`desc-${desc.substring(0,20)}-${idx}`} className="flex items-start justify-between bg-slate-800/50 rounded-lg px-4 py-3 gap-3">
                          <span className="text-slate-200 text-sm">{desc}</span>
                          <CopyButton text={desc} id={`desc-${idx}`} small />
                        </div>
                      ))}
                    </div>
                    <p className="px-5 pb-4 text-slate-500 text-xs">Max 90 characters each</p>
                  </div>

                  {/* Keywords */}
                  {results.google_ads.keywords && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800">
                        <h3 className="text-white font-semibold">Keywords</h3>
                      </div>
                      <div className="p-5 space-y-4">
                        {/* Exact Match */}
                        {results.google_ads.keywords.exact?.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm text-slate-400">Exact Match</span>
                              <button
                                onClick={() => copyAllToClipboard(results.google_ads.keywords.exact, "exact keywords")}
                                className="text-blue-400 text-xs hover:text-blue-300"
                              >
                                Copy All
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {results.google_ads.keywords.exact.map((kw, idx) => (
                                <span key={`item-${idx}`} className="px-3 py-1 bg-green-500/10 border border-green-500/20 text-green-400 rounded-lg text-sm cursor-pointer hover:bg-green-500/20"
                                  onClick={() => copyToClipboard(kw, `exact-${idx}`)}>
                                  {kw}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Phrase Match */}
                        {results.google_ads.keywords.phrase?.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm text-slate-400">Phrase Match</span>
                              <button
                                onClick={() => copyAllToClipboard(results.google_ads.keywords.phrase, "phrase keywords")}
                                className="text-blue-400 text-xs hover:text-blue-300"
                              >
                                Copy All
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {results.google_ads.keywords.phrase.map((kw, idx) => (
                                <span key={`item-${idx}`} className="px-3 py-1 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-lg text-sm cursor-pointer hover:bg-blue-500/20"
                                  onClick={() => copyToClipboard(kw, `phrase-${idx}`)}>
                                  {kw}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Broad Match */}
                        {results.google_ads.keywords.broad?.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm text-slate-400">Broad Match</span>
                              <button
                                onClick={() => copyAllToClipboard(results.google_ads.keywords.broad, "broad keywords")}
                                className="text-blue-400 text-xs hover:text-blue-300"
                              >
                                Copy All
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {results.google_ads.keywords.broad.map((kw, idx) => (
                                <span key={`item-${idx}`} className="px-3 py-1 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded-lg text-sm cursor-pointer hover:bg-purple-500/20"
                                  onClick={() => copyToClipboard(kw, `broad-${idx}`)}>
                                  {kw}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Negative Keywords */}
                  {results.google_ads.negative_keywords?.length > 0 && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
                        <h3 className="text-white font-semibold">Negative Keywords</h3>
                        <button
                          onClick={() => copyAllToClipboard(results.google_ads.negative_keywords, "negative keywords")}
                          className="text-blue-400 text-sm hover:text-blue-300 flex items-center gap-1"
                        >
                          <Copy className="w-4 h-4" /> Copy All
                        </button>
                      </div>
                      <div className="p-5">
                        <div className="flex flex-wrap gap-2">
                          {results.google_ads.negative_keywords.map((kw, idx) => (
                            <span key={`item-${idx}`} className="px-3 py-1 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-sm">
                              -{kw}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Ad Groups */}
                  {results.google_ads.ad_groups?.length > 0 && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800">
                        <h3 className="text-white font-semibold">Ad Groups Structure</h3>
                      </div>
                      <div className="p-5 grid md:grid-cols-2 gap-4">
                        {results.google_ads.ad_groups.map((group, idx) => (
                          <div key={`item-${idx}`} className="bg-slate-800/50 rounded-lg p-4">
                            <h4 className="text-blue-400 font-medium mb-1">{group.name}</h4>
                            <p className="text-slate-400 text-sm mb-2">{group.focus}</p>
                            <div className="flex flex-wrap gap-1">
                              {group.keywords?.map((kw, ki) => (
                                <span key={ki} className="px-2 py-0.5 bg-slate-700 text-slate-300 rounded text-xs">{kw}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Extensions */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Callout Extensions */}
                    {results.google_ads.callout_extensions?.length > 0 && (
                      <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
                          <h3 className="text-white font-semibold">Callout Extensions</h3>
                          <button
                            onClick={() => copyAllToClipboard(results.google_ads.callout_extensions, "callouts")}
                            className="text-blue-400 text-xs hover:text-blue-300"
                          >
                            Copy All
                          </button>
                        </div>
                        <div className="p-5">
                          <div className="flex flex-wrap gap-2">
                            {results.google_ads.callout_extensions.map((callout, idx) => (
                              <span key={`item-${idx}`} className="px-3 py-1 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-lg text-sm">
                                {callout}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Structured Snippets */}
                    {results.google_ads.structured_snippets && (
                      <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                        <div className="px-5 py-3 border-b border-slate-800">
                          <h3 className="text-white font-semibold">Structured Snippets</h3>
                        </div>
                        <div className="p-5">
                          <p className="text-slate-400 text-sm mb-2">Header: <span className="text-white">{results.google_ads.structured_snippets.header}</span></p>
                          <div className="flex flex-wrap gap-2">
                            {results.google_ads.structured_snippets.values?.map((value, idx) => (
                              <span key={`item-${idx}`} className="px-3 py-1 bg-slate-700 text-slate-300 rounded-lg text-sm">
                                {value}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Strategy & Budget */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {results.google_ads.bidding_strategy && (
                      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                        <h3 className="text-white font-semibold mb-3">Bidding Strategy</h3>
                        <p className="text-slate-300 text-sm">{results.google_ads.bidding_strategy}</p>
                      </div>
                    )}
                    {results.google_ads.daily_budget && (
                      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                        <h3 className="text-white font-semibold mb-3">Suggested Daily Budget</h3>
                        <p className="text-slate-300 text-sm">{results.google_ads.daily_budget}</p>
                      </div>
                    )}
                  </div>

                  {/* Landing Page Tips */}
                  {results.google_ads.landing_page_tips?.length > 0 && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                      <h3 className="text-white font-semibold mb-3">Landing Page Optimization Tips</h3>
                      <ul className="space-y-2">
                        {results.google_ads.landing_page_tips.map((tip, idx) => (
                          <li key={`item-${idx}`} className="flex items-start gap-2 text-slate-300 text-sm">
                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Facebook Ads Tab */}
              {activeTab === "facebook" && results.facebook_ads && (
                <div className="space-y-6">
                  {/* Primary Text */}
                  {results.facebook_ads.primary_text && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800 bg-blue-500/5">
                        <h3 className="text-white font-semibold flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-400" />
                          Primary Text Options
                        </h3>
                      </div>
                      <div className="p-5 space-y-4">
                        {["short", "medium", "long"].map((length) => (
                          results.facebook_ads.primary_text[length] && (
                            <div key={length} className="bg-slate-800/50 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-slate-400 text-sm capitalize">{length}</span>
                                <CopyButton text={results.facebook_ads.primary_text[length]} id={`primary-${length}`} small />
                              </div>
                              <p className="text-slate-200">{results.facebook_ads.primary_text[length]}</p>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Headlines */}
                  {results.facebook_ads.headlines?.length > 0 && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
                        <h3 className="text-white font-semibold">Headlines</h3>
                        <button
                          onClick={() => copyAllToClipboard(results.facebook_ads.headlines, "headlines")}
                          className="text-blue-400 text-sm hover:text-blue-300 flex items-center gap-1"
                        >
                          <Copy className="w-4 h-4" /> Copy All
                        </button>
                      </div>
                      <div className="p-5 space-y-2">
                        {results.facebook_ads.headlines.map((headline, idx) => (
                          <div key={`item-${idx}`} className="flex items-center justify-between bg-slate-800/50 rounded-lg px-4 py-3">
                            <span className="text-slate-200">{headline}</span>
                            <CopyButton text={headline} id={`fb-headline-${idx}`} small />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Descriptions */}
                  {results.facebook_ads.descriptions?.length > 0 && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
                        <h3 className="text-white font-semibold">Link Descriptions</h3>
                        <button
                          onClick={() => copyAllToClipboard(results.facebook_ads.descriptions, "descriptions")}
                          className="text-blue-400 text-sm hover:text-blue-300 flex items-center gap-1"
                        >
                          <Copy className="w-4 h-4" /> Copy All
                        </button>
                      </div>
                      <div className="p-5 space-y-2">
                        {results.facebook_ads.descriptions.map((desc, idx) => (
                          <div key={`item-${idx}`} className="flex items-center justify-between bg-slate-800/50 rounded-lg px-4 py-3">
                            <span className="text-slate-200 text-sm">{desc}</span>
                            <CopyButton text={desc} id={`fb-desc-${idx}`} small />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CTA Buttons & Audiences */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* CTA Buttons */}
                    {results.facebook_ads.cta_buttons?.length > 0 && (
                      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                        <h3 className="text-white font-semibold mb-3">Recommended CTA Buttons</h3>
                        <div className="flex flex-wrap gap-2">
                          {results.facebook_ads.cta_buttons.map((cta, idx) => (
                            <span key={`item-${idx}`} className="px-4 py-2 bg-blue-500/20 border border-blue-500/30 text-blue-400 rounded-lg font-medium">
                              {cta}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Demographics */}
                    {results.facebook_ads.audiences?.demographics && (
                      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                        <h3 className="text-white font-semibold mb-3">Demographics</h3>
                        <p className="text-slate-300 text-sm">{results.facebook_ads.audiences.demographics}</p>
                      </div>
                    )}
                  </div>

                  {/* Audience Targeting */}
                  {results.facebook_ads.audiences && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800">
                        <h3 className="text-white font-semibold">Audience Targeting Suggestions</h3>
                      </div>
                      <div className="p-5 space-y-4">
                        {/* Local Targeting */}
                        {results.facebook_ads.audiences.local && (
                          <div className="bg-slate-800/50 rounded-lg p-4">
                            <h4 className="text-blue-400 font-medium mb-2">Local Targeting</h4>
                            <p className="text-slate-300 text-sm">
                              {results.facebook_ads.audiences.local.location} 
                              {results.facebook_ads.audiences.local.radius && ` (${results.facebook_ads.audiences.local.radius} radius)`}
                            </p>
                          </div>
                        )}
                        
                        {/* Interest Targeting */}
                        {results.facebook_ads.audiences.interests?.length > 0 && (
                          <div>
                            <h4 className="text-slate-400 text-sm mb-2">Interest Targeting</h4>
                            <div className="flex flex-wrap gap-2">
                              {results.facebook_ads.audiences.interests.map((interest, idx) => (
                                <span key={`item-${idx}`} className="px-3 py-1 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-lg text-sm">
                                  {interest}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Creative Suggestions */}
                  {results.facebook_ads.creative_suggestions?.length > 0 && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800">
                        <h3 className="text-white font-semibold">Creative Suggestions</h3>
                      </div>
                      <div className="p-5 grid md:grid-cols-3 gap-4">
                        {results.facebook_ads.creative_suggestions.map((creative, idx) => (
                          <div key={`item-${idx}`} className="bg-slate-800/50 rounded-lg p-4">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs mb-2 ${
                              creative.type === 'video' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                            }`}>
                              {creative.type}
                            </span>
                            <p className="text-slate-300 text-sm">{creative.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Social Media Tab */}
              {activeTab === "social" && results.social_media && (
                <div className="space-y-6">
                  {/* Facebook Posts */}
                  {results.social_media.facebook_posts && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800 bg-pink-500/5">
                        <h3 className="text-white font-semibold flex items-center gap-2">
                          <Users className="w-4 h-4 text-pink-400" />
                          Facebook Posts
                        </h3>
                      </div>
                      <div className="p-5 space-y-4">
                        {["short", "medium", "long"].map((length) => (
                          results.social_media.facebook_posts[length] && (
                            <div key={length} className="bg-slate-800/50 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-slate-400 text-sm capitalize">{length}</span>
                                <CopyButton text={results.social_media.facebook_posts[length]} id={`fb-post-${length}`} small />
                              </div>
                              <p className="text-slate-200 whitespace-pre-wrap">{results.social_media.facebook_posts[length]}</p>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Instagram Captions */}
                  {results.social_media.instagram_captions?.length > 0 && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
                        <h3 className="text-white font-semibold flex items-center gap-2">
                          <ImageIcon className="w-4 h-4 text-pink-400" />
                          Instagram Captions
                        </h3>
                        <button
                          onClick={() => copyAllToClipboard(results.social_media.instagram_captions, "Instagram captions")}
                          className="text-pink-400 text-sm hover:text-pink-300 flex items-center gap-1"
                        >
                          <Copy className="w-4 h-4" /> Copy All
                        </button>
                      </div>
                      <div className="p-5 space-y-3">
                        {results.social_media.instagram_captions.map((caption, idx) => (
                          <div key={`item-${idx}`} className="bg-slate-800/50 rounded-lg p-4">
                            <div className="flex items-start justify-between gap-3">
                              <p className="text-slate-200 whitespace-pre-wrap flex-1">{caption}</p>
                              <CopyButton text={caption} id={`ig-caption-${idx}`} small />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hashtags */}
                  {results.social_media.hashtags && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800">
                        <h3 className="text-white font-semibold flex items-center gap-2">
                          <Hash className="w-4 h-4 text-pink-400" />
                          Hashtag Suggestions
                        </h3>
                      </div>
                      <div className="p-5 space-y-4">
                        {/* Primary Hashtags */}
                        {results.social_media.hashtags.primary?.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm text-slate-400">Primary / Branded</span>
                              <button
                                onClick={() => copyAllToClipboard(results.social_media.hashtags.primary.join(" "), "primary hashtags")}
                                className="text-pink-400 text-xs hover:text-pink-300"
                              >
                                Copy All
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {results.social_media.hashtags.primary.map((tag, idx) => (
                                <span key={`item-${idx}`} className="px-3 py-1 bg-pink-500/10 border border-pink-500/20 text-pink-400 rounded-lg text-sm cursor-pointer hover:bg-pink-500/20"
                                  onClick={() => copyToClipboard(tag, `primary-tag-${idx}`)}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Secondary Hashtags */}
                        {results.social_media.hashtags.secondary?.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm text-slate-400">Industry / Niche</span>
                              <button
                                onClick={() => copyAllToClipboard(results.social_media.hashtags.secondary.join(" "), "secondary hashtags")}
                                className="text-pink-400 text-xs hover:text-pink-300"
                              >
                                Copy All
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {results.social_media.hashtags.secondary.map((tag, idx) => (
                                <span key={`item-${idx}`} className="px-3 py-1 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded-lg text-sm cursor-pointer hover:bg-purple-500/20"
                                  onClick={() => copyToClipboard(tag, `secondary-tag-${idx}`)}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Trending Hashtags */}
                        {results.social_media.hashtags.trending?.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm text-slate-400">Trending / Popular</span>
                              <button
                                onClick={() => copyAllToClipboard(results.social_media.hashtags.trending.join(" "), "trending hashtags")}
                                className="text-pink-400 text-xs hover:text-pink-300"
                              >
                                Copy All
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {results.social_media.hashtags.trending.map((tag, idx) => (
                                <span key={`item-${idx}`} className="px-3 py-1 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-lg text-sm cursor-pointer hover:bg-blue-500/20"
                                  onClick={() => copyToClipboard(tag, `trending-tag-${idx}`)}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Carousel Ideas */}
                  {results.social_media.carousel_ideas?.length > 0 && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800">
                        <h3 className="text-white font-semibold">Carousel Post Ideas</h3>
                      </div>
                      <div className="p-5 space-y-4">
                        {results.social_media.carousel_ideas.map((carousel, idx) => (
                          <div key={`item-${idx}`} className="bg-slate-800/50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-pink-400 font-medium">{carousel.title}</h4>
                              <CopyButton text={carousel.slides?.join("\n\n") || ""} id={`carousel-${idx}`} small />
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                              {carousel.slides?.map((slide, si) => (
                                <div key={si} className="bg-slate-700/50 rounded-lg p-3 text-center">
                                  <span className="text-xs text-slate-500 block mb-1">Slide {si + 1}</span>
                                  <p className="text-slate-300 text-sm">{slide}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Story/Reel Ideas */}
                  {results.social_media.story_reels?.length > 0 && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800">
                        <h3 className="text-white font-semibold">Story & Reel Ideas</h3>
                      </div>
                      <div className="p-5 grid md:grid-cols-2 gap-4">
                        {results.social_media.story_reels.map((item, idx) => (
                          <div key={`item-${idx}`} className="bg-slate-800/50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                item.type === 'reel' ? 'bg-red-500/20 text-red-400' : 'bg-purple-500/20 text-purple-400'
                              }`}>
                                {item.type}
                              </span>
                              <CopyButton text={`${item.hook}\n\n${item.caption}\n\nCTA: ${item.cta}`} id={`story-${idx}`} small />
                            </div>
                            <p className="text-pink-400 text-sm font-medium mb-1">Hook: {item.hook}</p>
                            <p className="text-slate-300 text-sm mb-2">{item.caption}</p>
                            <p className="text-slate-500 text-xs">CTA: {item.cta}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Promotional Posts */}
                  {results.social_media.promotional_posts?.length > 0 && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800 bg-green-500/5">
                        <h3 className="text-white font-semibold flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-green-400" />
                          Promotional Posts
                        </h3>
                      </div>
                      <div className="p-5 space-y-4">
                        {results.social_media.promotional_posts.map((post, idx) => (
                          <div key={`item-${idx}`} className="bg-slate-800/50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-green-400 font-medium">{post.title}</h4>
                              <CopyButton text={post.content} id={`promo-${idx}`} small />
                            </div>
                            <p className="text-slate-200 whitespace-pre-wrap mb-2">{post.content}</p>
                            {post.best_time && (
                              <p className="text-slate-500 text-xs flex items-center gap-1">
                                <Clock className="w-3 h-3" /> Best time: {post.best_time}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Educational Posts */}
                  {results.social_media.educational_posts?.length > 0 && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800 bg-blue-500/5">
                        <h3 className="text-white font-semibold flex items-center gap-2">
                          <Zap className="w-4 h-4 text-blue-400" />
                          Educational Posts
                        </h3>
                      </div>
                      <div className="p-5 space-y-4">
                        {results.social_media.educational_posts.map((post, idx) => (
                          <div key={`item-${idx}`} className="bg-slate-800/50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-blue-400 font-medium">{post.title}</h4>
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-0.5 bg-slate-700 text-slate-400 rounded text-xs">{post.format}</span>
                                <CopyButton text={post.content} id={`edu-${idx}`} small />
                              </div>
                            </div>
                            <p className="text-slate-200 whitespace-pre-wrap">{post.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Seasonal Posts */}
                  {results.social_media.seasonal_posts?.length > 0 && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800 bg-orange-500/5">
                        <h3 className="text-white font-semibold flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-orange-400" />
                          Seasonal Posts
                        </h3>
                      </div>
                      <div className="p-5 grid md:grid-cols-2 gap-4">
                        {results.social_media.seasonal_posts.map((post, idx) => (
                          <div key={`item-${idx}`} className="bg-slate-800/50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-orange-400 font-medium">{post.occasion}</span>
                              <CopyButton text={post.content} id={`seasonal-${idx}`} small />
                            </div>
                            <p className="text-slate-200 text-sm whitespace-pre-wrap mb-2">{post.content}</p>
                            {post.hashtags?.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {post.hashtags.map((tag, ti) => (
                                  <span key={ti} className="text-orange-400/70 text-xs">{tag}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CTA Posts */}
                  {results.social_media.cta_posts?.length > 0 && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
                      <div className="px-5 py-3 border-b border-slate-800 bg-red-500/5">
                        <h3 className="text-white font-semibold flex items-center gap-2">
                          <Target className="w-4 h-4 text-red-400" />
                          Call-to-Action Posts
                        </h3>
                      </div>
                      <div className="p-5 grid md:grid-cols-2 gap-4">
                        {results.social_media.cta_posts.map((post, idx) => (
                          <div key={`item-${idx}`} className="bg-slate-800/50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs uppercase">{post.type}</span>
                              <CopyButton text={post.content} id={`cta-${idx}`} small />
                            </div>
                            <p className="text-slate-200 text-sm whitespace-pre-wrap mb-2">{post.content}</p>
                            {post.urgency && (
                              <p className="text-red-400/70 text-xs">Urgency: {post.urgency}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </section>
      )}

      {/* Features Section - When No Results */}
      {!results && !loading && (
        <section className="py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-white text-center mb-3">What You'll Get</h2>
            <p className="text-slate-400 text-center mb-10">Complete ad campaigns and social media content ready to launch</p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {/* Google Ads Output */}
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                    <Target className="w-5 h-5 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Google Ads</h3>
                </div>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> 10-15 Headlines (30 chars)</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> 2-4 Descriptions (90 chars)</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> Keywords (exact, phrase, broad)</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> Negative keywords</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> Ad groups by service type</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> Callout extensions</li>
                </ul>
              </div>

              {/* Facebook Ads Output */}
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Facebook Ads</h3>
                </div>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> Primary text (short, medium, long)</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> 3 Headlines & Descriptions</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> Recommended CTA buttons</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> Audience targeting</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> Interest suggestions</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-400" /> Creative suggestions</li>
                </ul>
              </div>

              {/* Social Media Output */}
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-pink-500/10 rounded-lg flex items-center justify-center">
                    <Share2 className="w-5 h-5 text-pink-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Social Media</h3>
                </div>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-pink-400" /> Facebook posts (3 lengths)</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-pink-400" /> Instagram captions</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-pink-400" /> Hashtag suggestions (25+)</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-pink-400" /> Carousel & Reel ideas</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-pink-400" /> Promotional & Educational posts</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-pink-400" /> Seasonal & CTA posts</li>
                </ul>
              </div>
            </div>

            {/* Who It's For */}
            <div className="text-center">
              <h3 className="text-lg font-semibold text-white mb-4">Who It's For</h3>
              <div className="flex flex-wrap justify-center gap-2">
                {targetAudiences.map((audience, idx) => (
                  <span key={`item-${idx}`} className="px-4 py-2 bg-slate-800/50 border border-slate-700 text-slate-300 rounded-lg text-sm">
                    {audience}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </MainLayout>
  );
};


export default PromoteYourSitePage;
